/**
 * This function takes a JSON string and ensures it is properly closed.
 * It handles open brackets, braces, and quotes, and adds the necessary closing characters.
 * Usable for JSON strings that are being streamed in chunks.
 * Doesn't always give parsable JSON! Use `try/catch` with `JSON.parse`.
 *
 * @param {string} json - The JSON string to finish.
 * @returns {string} The finished JSON string.
 */
export function finishJSON(json: string): string {
    const openChars = [];
    const closingChars = [];
    let inString = false;

    for (let i = 0; i < json.length; i++) {
        const char = json[i];

        if ((char === '{' || char === '[') && !inString) {
            openChars.push(char);
        } else if ((char === '}' || char === ']') && !inString) {
            openChars.pop();
        } else if (char === '"' && (i === 0 || json[i - 1] !== '\\')) {
            if (inString) {
                openChars.pop();
                inString = false;
            } else {
                openChars.push('"');
                inString = true;
            }
        }
    }

    while (openChars.length > 0) {
        const openingChar = openChars.pop();
        switch (openingChar) {
            case '{':
                closingChars.push('}');
                break;
            case '[':
                closingChars.push(']');
                break;
            case '"':
                closingChars.push('"');
                break;
        }
    }

    json += closingChars.join('');

    return json;
}

export function tryParseJSON<T>(json: string): T | null {
    try {
        return JSON.parse(json) as T;
    } catch (e) {
        return null;
    }
}
